import { invoke } from "@tauri-apps/api/core";

import type { BranchInfo as GitBranchInfo } from "~/state/machines/git/gitBranches.machine";

type RustGitRemote = {
	url: string;
	name: string;
};

type RustGitRepoHistory = {
	commits: {
		message: string;
		date: string;
		hash: string;
		author: string;
	}[];
};

type RustGitRepoStatus = {
	ahead: number;
	behind: number;
	branch: string;
	commit: string;
	files: {
		from?: string;
		path?: string;
		index?: string;
		working_dir?: string;
	}[];
	is_clean: boolean;
	is_repo: boolean;
	staged: string[];
	changed: number;
	insertions: number;
	deletions: number;
};

type RustGitShow = {
	header: {
		author: {
			name: string;
			email: string;
		};
		commit_sha: string;
		parents: string[];
		committer: {
			name: string;
			email: string;
		};
		commit_time: string;
		commit_message: string;
	};
	contents: string[];
	original_diff: string;
	parsed_files: {
		type: "added" | "removed" | "renamed" | "modified";
		aPath: string;
		bPath: string;
		hunks: {
			aLineStart: number;
			aNumLines: number;
			bLineStart: number;
			bNumLines?: number;
			lines: {
				type: "insertion" | "deletion" | "context";
				content: string;
			}[];
		}[];
	}[];
};

/**
 * # Tauri API
 *
 * This is the API that is used to interact with the Tauri backend.
 */
export const tauriApi = {
	clone: (params: { path: string; remoteUrl: string; token: string | undefined }) => {
		return invoke("clone", params);
	},
	commit: (params: { path: string; message: string }) => {
		return invoke<void>("commit", params);
	},
	config: (params: { path: string }) => {
		return invoke<Record<string, string | number | null>>("config", params);
	},
	configSet: (params: { path: string; newConfig: Record<string, string | number | null> }) => {
		return invoke<void>("set_config", params);
	},
	diff: (params: { path: string }) => {
		return invoke<void>("diff", params);
	},
	fetch: (params: {
		path: string;
		remote: string;
		branch: string;
		token: string | undefined;
	}) => {
		return invoke<void>("fetch", params);
	},
	init: (params: { path: string }) => {
		return invoke<void>("init_repo", params);
	},
	pull: (params: { path: string; token: string | undefined }) => {
		return invoke<void>("pull_repo", params);
	},
	push: (params: { path: string; remote: string; branch: string; token: string | undefined }) => {
		return invoke<void>("push", params);
	},
	remoteAdd: (params: { path: string; remote: string; url: string }) => {
		return invoke<void>("add_remote", params);
	},
	remoteList: (params: { path: string }) => {
		return invoke<RustGitRemote[]>("list_remotes", params);
	},
	repoHistory: (params: { path: string }) => {
		return invoke<RustGitRepoHistory>("repo_history", params);
	},
	branchList: (params: { path: string }) => {
		return invoke<GitBranchInfo[]>("list_local_branches", params);
	},
	branchTrack: (params: {
		path: string;
		remote: string;
		localBranch: string;
		upstreamBranch: string;
		token: string | undefined;
	}) => {
		const { path, remote, localBranch, upstreamBranch, token } = params;
		return invoke<void>("push", { path, remote, branch: localBranch, token }).then(() =>
			invoke<void>("add_upstream_branch", { path, localBranch, remote, upstreamBranch })
		);
	},
	show: (params: { path: string; commitHash: string }) => {
		return invoke<RustGitShow>("show", params);
	},
	stage: (params: { path: string; files: string[]; operation: "add" | "reset" }) => {
		return invoke<void>("stage", params);
	},
	status: (params: { path: string }) => {
		return invoke<RustGitRepoStatus>("status", params);
	},
};

export type TauriApi = typeof tauriApi;
